import { inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import {
  ApplicationInsights,
  eSeverityLevel,
  ICustomProperties,
  Snippet
} from '@microsoft/applicationinsights-web';
import { onCLS, onINP, onLCP, onTTFB } from 'web-vitals';

export interface TelemetryConfig {
  connectionString?: string;
  roleName: string;
  roleInstance: string;
}

export const APP_INSIGHTS_FACTORY = new InjectionToken<(config: Snippet) => ApplicationInsights>(
  'APP_INSIGHTS_FACTORY'
);

export const TELEMETRY_CONFIG = new InjectionToken<TelemetryConfig | undefined>('TELEMETRY_CONFIG');

@Injectable({ providedIn: 'root' })
export class ApplicationInsightsService {
  private readonly router = inject(Router);
  private appInsights!: ApplicationInsights;
  private readonly factory = inject(APP_INSIGHTS_FACTORY);
  private readonly config = inject(TELEMETRY_CONFIG);

  private readonly sessionId = sessionStorage.getItem('telemetry-session-id') ?? crypto.randomUUID();

  init() {
    if (!this.config) {
      return;
    }

    const connectionString = this.config?.connectionString?.trim();

    if (!connectionString) {
      return;
    }

    const angularPlugin = new AngularPlugin();

    sessionStorage.setItem('telemetry-session-id', this.sessionId);

    this.appInsights = this.factory({
      config: {
        connectionString: this.config.connectionString,
        extensions: [angularPlugin],
        extensionConfig: {
          [angularPlugin.identifier]: {
            router: this.router
          }
        },
        enableAutoRouteTracking: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        disableAjaxTracking: false,
        disableFetchTracking: false,
        disablePageUnloadEvents: ['unload', 'beforeunload']
      }
    });

    this.appInsights.loadAppInsights();

    this.appInsights.addTelemetryInitializer(envelope => {
      const tags = envelope.tags || (envelope.tags = {});
      tags['ai.cloud.role'] = this.config?.roleName;
      tags['ai.cloud.roleInstance'] = this.config?.roleInstance;

      const baseData = envelope.baseData;

      if (baseData) {
        baseData.properties = {
          ...baseData.properties,
          sessionId: this.sessionId,
          currentRoute: this.router.url,
          application: this.config?.roleInstance,
          environment: window.location.hostname,
          screenWidth: window.screen.width.toString(),
          screenHeight: window.screen.height.toString()
        };
      }

      return true;
    });

    this.captureWebVitals();
    this.captureConsoleErrors();
  }

  private captureWebVitals() {
    const appInsights = this.appInsights;

    /**
     * Largest Contentful Paint (LCP) measures the time it takes for the largest content element on a web page to become visible to the user.
     * A high LCP can indicate that the page is slow to load, which can lead to a poor user experience.
     * Monitoring LCP can help identify and address performance issues that affect page load times.
     *
     * https://web.dev/articles/lcp
     */
    onLCP(metric => {
      if (metric.value > 2500) {
        appInsights.trackTrace({
          message: `Poor LCP: ${metric.value}`,
          severityLevel: eSeverityLevel.Warning
        });
      }
      appInsights.trackMetric({ name: metric.name, average: metric.value });
    });

    /**
     * Cumulative Layout Shift (CLS) measures the visual stability of a web page by tracking unexpected layout shifts that occur during the loading and interaction phases.
     * A high CLS can indicate a poor user experience, as it can lead to users clicking on the wrong elements or losing their place on the page.
     * Monitoring CLS can help identify and address issues that affect the visual stability of the application.
     *
     * https://web.dev/articles/cls
     */
    onCLS(metric => {
      if (metric.value > 0.1) {
        appInsights.trackTrace({
          message: `Poor CLS: ${metric.value}`,
          severityLevel: eSeverityLevel.Warning
        });
      }
      appInsights.trackMetric({ name: metric.name, average: metric.value });
    });

    /**
     * Interaction to Next Paint (INP) measures the responsiveness of a web application by tracking the time it takes for the application to respond to user interactions, such as clicks or keyboard input.
     * A high INP can indicate that the application is slow to respond to user interactions, which can lead to a poor user experience.
     * Monitoring INP can help identify and address performance issues that affect the responsiveness of the application.
     *
     * https://web.dev/articles/inp
     */
    onINP(metric => {
      if (metric.value > 200) {
        appInsights.trackTrace({
          message: `Poor INP: ${metric.value}`,
          severityLevel: eSeverityLevel.Warning
        });
      }
      appInsights.trackMetric({ name: metric.name, average: metric.value });
    });

    /**
     * Time to First Byte (TTFB) is the time it takes for the browser to receive the first byte of the response from the server after making a request.
     * A high TTFB can indicate server-side performance issues, such as slow database queries, inefficient server-side code, or network latency.
     * Monitoring TTFB can help identify and address these issues to improve overall application performance.
     *
     * https://web.dev/articles/ttfb
     */
    onTTFB(metric => {
      if (metric.value > 600) {
        appInsights.trackTrace({
          message: `Poor TTFB: ${metric.value}`,
          severityLevel: eSeverityLevel.Warning
        });
      }
      appInsights.trackMetric({ name: metric.name, average: metric.value });
    });
  }

  private captureConsoleErrors() {
    const originalError = console.error;

    console.error = (...args: unknown[]) => {
      originalError.apply(console, args);

      const error = args.find(a => a instanceof Error) ?? new Error(args.join(' '));

      this.appInsights.trackException({
        exception: error,
        severityLevel: eSeverityLevel.Error
      });
    };
  }

  private trackEvent(name: string, properties?: ICustomProperties) {
    this.appInsights.trackEvent(
      { name },
      {
        route: this.router.url,
        ...properties
      }
    );
  }

  trackBusinessEvent(
    name: string,
    properties: ICustomProperties & {
      feature: string;
      step?: string;
      outcome?: 'success' | 'failure';
      errorCode?: string;
      variant?: string;
    }
  ) {
    this.trackEvent(name, { ...properties, application: this.config?.roleInstance });
  }

  trackTrace(message: string, properties?: ICustomProperties) {
    this.appInsights.trackTrace(
      {
        message
      },
      {
        route: this.router.url,
        ...properties
      }
    );
  }

  trackException(error: Error) {
    this.appInsights.trackException({
      exception: error,
      properties: {
        route: this.router.url
      }
    });
  }

  setUser(userId: string, accountId?: string) {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearUser() {
    if (!this.appInsights) {
      return;
    }

    this.appInsights.clearAuthenticatedUserContext();
  }
}
